import { useState, useEffect, useRef } from 'react'
import { useDebounce } from 'hooks/useDebounce'
import { fetchCollectionSearchApi } from 'hooks/collection'

type SearchableCollectionDropdownProps = {
    value?: any
    onSelect: (item: any) => void
    placeholder?: string
}

export default function SearchableCollectionDropdown({
    value,
    onSelect,
    placeholder = "Search collection...",
}: SearchableCollectionDropdownProps) {
    const [query, setQuery] = useState(value?.name || "")
    const [options, setOptions] = useState<any[]>([])
    const [open, setOpen] = useState(false)
    const [loading, setLoading] = useState(false)

    const isUserTypingRef = useRef(false)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const debouncedQuery = useDebounce(query, 400)

    /* 🔄 Sync input when value changes (prefill / edit mode) */
    // useEffect(() => {
    //     if (value?.name && !isUserTypingRef.current) {
    //         setQuery(value.name)
    //     } else if (!value && !isUserTypingRef.current) {
    //         setQuery("")
    //     }
    // }, [value?.id, value?.name])

    useEffect(() => {
        if (isUserTypingRef.current) return

        if (value?.name && query !== value.name) {
            setQuery(value.name)
        }

        if (!value && query !== "") {
            setQuery("")
        }
    }, [value?.id, value?.name])



    /* 🔍 Fetch collections */
    useEffect(() => {
        if (!debouncedQuery.trim()) {
            setOptions([])
            return
        }

        const load = async () => {
            try {
                setLoading(true)
                const res = await fetchCollectionSearchApi(debouncedQuery)
                setOptions(res || [])
            } catch (err) {
                console.error(err)
            } finally {
                setLoading(false)
            }
        }

        load()
    }, [debouncedQuery])

    /* Close dropdown on outside click */
    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (!dropdownRef.current?.contains(e.target as Node)) {
                setOpen(false)
            }
        }
        document.addEventListener("mousedown", handler)
        return () => document.removeEventListener("mousedown", handler)
    }, [])

    const handleSelect = (item: any) => {
        isUserTypingRef.current = false
        onSelect(item)
        setQuery(item.name)
        setOpen(false)
    }

    return (
        <div ref={dropdownRef} className="relative w-full">
            <input
                value={query}
                placeholder={placeholder}
                onChange={(e) => {
                    isUserTypingRef.current = true
                    setQuery(e.target.value)
                    setOpen(true)
                    onSelect(null)
                }}
                onFocus={() => {
                    setOpen(true)
                    if (value?.name && query === value.name) {
                        isUserTypingRef.current = false
                    }
                }}
                onBlur={() => {
                    setTimeout(() => {
                        isUserTypingRef.current = false
                    }, 200)
                }}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm
                text-slate-900 placeholder-slate-400
                focus:outline-none focus:ring-2 focus:ring-blue-500
                dark:border-slate-600 dark:bg-slate-800 dark:text-white"
            />

            {open && (
                <div className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md
                border border-slate-200 bg-white shadow-lg
                dark:bg-slate-800 dark:border-slate-600">
                    {loading && (
                        <div className="px-3 py-2 text-sm text-slate-500">
                            Searching...
                        </div>
                    )}

                    {!loading && options.length === 0 && (
                        <div className="px-3 py-2 text-sm text-slate-500">
                            No results found
                        </div>
                    )}

                    {options.map((item) => (
                        <div
                            key={item.id}
                            onClick={() => handleSelect(item)}
                            className="cursor-pointer px-3 py-2 text-sm
                            hover:bg-blue-50 dark:hover:bg-slate-700"
                        >
                            {item.name}
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
